/**
 * FikoPdfChannel - Pencereler Arası İletişim Servisi
 * Ana sayfa ile PDF editör arasında BroadcastChannel ile haberleşme
 *
 * @author FikoYT
 * @version 1.0.0
 */

import { Subject, Observable, firstValueFrom, timeout, catchError, of } from 'rxjs';
import { filter, take, map } from 'rxjs/operators';
import {
  ChannelMessage,
  ChannelMessageType,
  ScreenshotRequest,
  ScreenshotResult,
  ModelInfo,
  MeasurementItem,
  SceneObjectInfo,
  CizimInfo
} from './FikoPdfTemplate';

const CHANNEL_NAME = 'fikoyt-pdf-editor-channel';
const REQUEST_TIMEOUT = 10000; // 10 saniye

/**
 * Ana sayfa tarafı (3D Viewer)
 * Screenshot, ölçüm ve model bilgisi isteklerini karşılar
 */
export class FikoPdfChannelHost {
  private channel: BroadcastChannel;
  private messageSubject = new Subject<ChannelMessage>();

  // Callback fonksiyonları (dışarıdan set edilir)
  public onScreenshotRequest?: (req: ScreenshotRequest) => Promise<ScreenshotResult>;
  public onMeasurementsRequest?: () => MeasurementItem[];
  public onModelInfoRequest?: () => ModelInfo | null;
  public onObjectListRequest?: () => SceneObjectInfo[];
  public onObjectInfoRequest?: (objectId: string) => ModelInfo | null;
  public onCizimListRequest?: () => CizimInfo[];
  public onStartSelectionMode?: () => void;  // Seçim modunu başlat
  public onCancelSelectionMode?: () => void; // Seçim modunu iptal et
  public onFocusViewer?: () => void;         // 3D pencereyi öne getir

  constructor() {
    this.channel = new BroadcastChannel(CHANNEL_NAME);
    this.channel.onmessage = (event) => this.handleMessage(event.data);
  }

  private handleMessage(msg: ChannelMessage): void {
    this.messageSubject.next(msg);

    switch (msg.type) {
      case 'ping':
        this.send({ type: 'pong' });
        break;

      case 'editor-ready':
        console.log('[FikoPdfChannelHost] Editör bağlandı');
        break;

      case 'editor-closed':
        console.log('[FikoPdfChannelHost] Editör kapatıldı');
        break;

      case 'request-screenshot':
        this.handleScreenshotRequest(msg);
        break;

      case 'request-measurements':
        this.handleMeasurementsRequest(msg);
        break;

      case 'request-model-info':
        this.handleModelInfoRequest(msg);
        break;

      case 'request-object-list':
        this.handleObjectListRequest(msg);
        break;

      case 'request-object-info':
        this.handleObjectInfoRequest(msg);
        break;

      case 'request-cizim-list':
        this.handleCizimListRequest(msg);
        break;

      case 'start-selection-mode':
        console.log('[FikoPdfChannelHost] Seçim modu başlatılıyor');
        this.onStartSelectionMode?.();
        break;

      case 'cancel-selection-mode':
        console.log('[FikoPdfChannelHost] Seçim modu iptal edildi');
        this.onCancelSelectionMode?.();
        break;

      case 'focus-viewer':
        console.log('[FikoPdfChannelHost] 3D pencere öne getiriliyor');
        this.onFocusViewer?.();
        break;
    }
  }

  /**
   * Seçim sonucunu PDF editöre gönder
   */
  sendSelectionResult(cizimInfo: CizimInfo | null): void {
    this.send({
      type: 'selection-result',
      payload: cizimInfo
    });
  }

  /**
   * PDF editörü öne getirmesi için mesaj gönder
   */
  requestFocusEditor(): void {
    this.send({ type: 'focus-editor' });
  }

  private async handleScreenshotRequest(msg: ChannelMessage): Promise<void> {
    if (!this.onScreenshotRequest) {
      console.warn('[FikoPdfChannelHost] Screenshot handler tanımlı değil');
      return;
    }

    try {
      const result = await this.onScreenshotRequest(msg.payload as ScreenshotRequest);
      this.send({
        type: 'screenshot-result',
        payload: result,
        requestId: msg.requestId
      });
    } catch (error) {
      console.error('[FikoPdfChannelHost] Screenshot hatası:', error);
      this.send({
        type: 'screenshot-result',
        payload: { error: 'Screenshot alınamadı' },
        requestId: msg.requestId
      });
    }
  }

  private handleMeasurementsRequest(msg: ChannelMessage): void {
    const measurements = this.onMeasurementsRequest?.() || [];
    this.send({
      type: 'measurements-result',
      payload: measurements,
      requestId: msg.requestId
    });
  }

  private handleModelInfoRequest(msg: ChannelMessage): void {
    const info = this.onModelInfoRequest?.() || null;
    this.send({
      type: 'model-info-result',
      payload: info,
      requestId: msg.requestId
    });
  }

  private handleObjectListRequest(msg: ChannelMessage): void {
    const objects = this.onObjectListRequest?.() || [];
    this.send({
      type: 'object-list-result',
      payload: objects,
      requestId: msg.requestId
    });
  }

  private handleObjectInfoRequest(msg: ChannelMessage): void {
    const objectId = msg.payload?.objectId;
    const info = objectId ? this.onObjectInfoRequest?.(objectId) : null;
    this.send({
      type: 'object-info-result',
      payload: info,
      requestId: msg.requestId
    });
  }

  private handleCizimListRequest(msg: ChannelMessage): void {
    const cizimler = this.onCizimListRequest?.() || [];
    this.send({
      type: 'cizim-list-result',
      payload: cizimler,
      requestId: msg.requestId
    });
  }

  send(msg: ChannelMessage): void {
    this.channel.postMessage(msg);
  }

  get messages$(): Observable<ChannelMessage> {
    return this.messageSubject.asObservable();
  }

  destroy(): void {
    this.channel.close();
    this.messageSubject.complete();
  }
}

/**
 * Editör tarafı (Yeni sekme)
 * Ana sayfadan veri ister
 */
export class FikoPdfChannelClient {
  private channel: BroadcastChannel;
  private messageSubject = new Subject<ChannelMessage>();
  private connected = false;

  constructor() {
    this.channel = new BroadcastChannel(CHANNEL_NAME);
    this.channel.onmessage = (event) => this.handleMessage(event.data);
  }

  private handleMessage(msg: ChannelMessage): void {
    this.messageSubject.next(msg);

    if (msg.type === 'pong') {
      this.connected = true;
    }
  }

  /**
   * Ana sayfaya bağlantıyı kontrol et
   */
  async checkConnection(): Promise<boolean> {
    this.send({ type: 'ping' });

    try {
      await firstValueFrom(
        this.messageSubject.pipe(
          filter(m => m.type === 'pong'),
          take(1),
          timeout(2000)
        )
      );
      this.connected = true;
      return true;
    } catch {
      this.connected = false;
      return false;
    }
  }

  /**
   * Editörün hazır olduğunu bildir
   */
  notifyReady(): void {
    this.send({ type: 'editor-ready' });
  }

  /**
   * Editörün kapandığını bildir
   */
  notifyClose(): void {
    this.send({ type: 'editor-closed' });
  }

  /**
   * Screenshot iste
   */
  async requestScreenshot(request: ScreenshotRequest): Promise<ScreenshotResult | null> {
    const requestId = this.generateRequestId();

    this.send({
      type: 'request-screenshot',
      payload: request,
      requestId
    });

    try {
      const response = await firstValueFrom(
        this.messageSubject.pipe(
          filter(m => m.type === 'screenshot-result' && m.requestId === requestId),
          take(1),
          timeout(REQUEST_TIMEOUT),
          map(m => m.payload as ScreenshotResult)
        )
      );
      return response;
    } catch (error) {
      console.error('[FikoPdfChannelClient] Screenshot timeout:', error);
      return null;
    }
  }

  /**
   * Ölçümleri iste
   */
  async requestMeasurements(): Promise<MeasurementItem[]> {
    const requestId = this.generateRequestId();

    this.send({
      type: 'request-measurements',
      requestId
    });

    try {
      const response = await firstValueFrom(
        this.messageSubject.pipe(
          filter(m => m.type === 'measurements-result' && m.requestId === requestId),
          take(1),
          timeout(REQUEST_TIMEOUT),
          map(m => m.payload as MeasurementItem[])
        )
      );
      return response || [];
    } catch {
      return [];
    }
  }

  /**
   * Model bilgisini iste
   */
  async requestModelInfo(): Promise<ModelInfo | null> {
    const requestId = this.generateRequestId();

    this.send({
      type: 'request-model-info',
      requestId
    });

    try {
      const response = await firstValueFrom(
        this.messageSubject.pipe(
          filter(m => m.type === 'model-info-result' && m.requestId === requestId),
          take(1),
          timeout(REQUEST_TIMEOUT),
          map(m => m.payload as ModelInfo)
        )
      );
      return response;
    } catch {
      return null;
    }
  }

  /**
   * Sahnedeki nesnelerin listesini iste
   */
  async requestObjectList(): Promise<SceneObjectInfo[]> {
    const requestId = this.generateRequestId();

    this.send({
      type: 'request-object-list',
      requestId
    });

    try {
      const response = await firstValueFrom(
        this.messageSubject.pipe(
          filter(m => m.type === 'object-list-result' && m.requestId === requestId),
          take(1),
          timeout(REQUEST_TIMEOUT),
          map(m => m.payload as SceneObjectInfo[])
        )
      );
      return response || [];
    } catch {
      return [];
    }
  }

  /**
   * Belirli bir nesnenin bilgisini iste
   */
  async requestObjectInfo(objectId: string): Promise<ModelInfo | null> {
    const requestId = this.generateRequestId();

    this.send({
      type: 'request-object-info',
      payload: { objectId },
      requestId
    });

    try {
      const response = await firstValueFrom(
        this.messageSubject.pipe(
          filter(m => m.type === 'object-info-result' && m.requestId === requestId),
          take(1),
          timeout(REQUEST_TIMEOUT),
          map(m => m.payload as ModelInfo)
        )
      );
      return response;
    } catch {
      return null;
    }
  }

  /**
   * FikoCizimYalin listesini iste
   */
  async requestCizimList(): Promise<CizimInfo[]> {
    const requestId = this.generateRequestId();

    this.send({
      type: 'request-cizim-list',
      requestId
    });

    try {
      const response = await firstValueFrom(
        this.messageSubject.pipe(
          filter(m => m.type === 'cizim-list-result' && m.requestId === requestId),
          take(1),
          timeout(REQUEST_TIMEOUT),
          map(m => m.payload as CizimInfo[])
        )
      );
      return response || [];
    } catch {
      return [];
    }
  }

  /**
   * 3D görüntüleyicide seçim modunu başlat
   * Kullanıcı bir nesne seçene kadar bekler
   * @returns Seçilen nesne bilgisi veya null (iptal edilirse)
   */
  async startSelectionMode(): Promise<CizimInfo | null> {
    // 3D pencereyi öne getir
    this.send({ type: 'focus-viewer' });

    // Seçim modunu başlat
    this.send({ type: 'start-selection-mode' });

    try {
      // Seçim sonucunu bekle (60 saniye timeout)
      const response = await firstValueFrom(
        this.messageSubject.pipe(
          filter(m => m.type === 'selection-result'),
          take(1),
          timeout(60000), // 60 saniye
          map(m => m.payload as CizimInfo | null)
        )
      );
      return response;
    } catch {
      // Timeout veya hata durumunda null döndür
      this.send({ type: 'cancel-selection-mode' });
      return null;
    }
  }

  /**
   * Seçim modunu iptal et
   */
  cancelSelectionMode(): void {
    this.send({ type: 'cancel-selection-mode' });
  }

  /**
   * 3D pencereyi öne getir
   */
  focusViewer(): void {
    this.send({ type: 'focus-viewer' });
  }

  /**
   * focus-editor mesajını dinle
   */
  onFocusEditor(callback: () => void): void {
    this.messageSubject.pipe(
      filter(m => m.type === 'focus-editor')
    ).subscribe(() => callback());
  }

  private send(msg: ChannelMessage): void {
    this.channel.postMessage(msg);
  }

  private generateRequestId(): string {
    return 'req_' + Date.now() + '_' + Math.random().toString(36).substring(2, 7);
  }

  get isConnected(): boolean {
    return this.connected;
  }

  get messages$(): Observable<ChannelMessage> {
    return this.messageSubject.asObservable();
  }

  destroy(): void {
    this.notifyClose();
    this.channel.close();
    this.messageSubject.complete();
  }
}

/**
 * PDF Editör penceresini aç
 * @param baseUrl Uygulama base URL'i
 * @returns Window referansı veya null
 */
export function openPdfEditorWindow(baseUrl: string = ''): Window | null {
  const url = `${baseUrl}/pdf-editor`;
  const features = [
    'width=1400',
    'height=900',
    'menubar=no',
    'toolbar=no',
    'location=no',
    'status=no',
    'resizable=yes',
    'scrollbars=yes'
  ].join(',');

  return window.open(url, 'FikoYT_PDF_Editor', features);
}
